import { MembershipTier } from 'modules/common/types';

export interface Address {
    street: string;
    unit: string;
    city: string;
    state: string;
    zipcode: string;
}

interface Attributes {
    ein_before_llc: boolean;
    is_community_property: boolean;
    wet_ink_signature_required: boolean;
    online_formation_allowed: boolean;
    naics_code_required: boolean;
    publication_required: boolean;
    state_scorp_form_required: boolean;
    business_license_required: boolean;
    passthrough_tax_eligible: boolean;
}

interface Content {
    articles_of_organization_name: string;
    additional_filing_doc_name: string;
    llc_filing_website: string;
    sos_business_name_check_website: string;
    federal_scorp_fax_number: string;
    state_scorp_fax_number: string;
}

interface StateOfInc {
    id: string;
    is_servicing: boolean;
    attributes: Attributes;
    content: Content;
    name: string;
}

export interface Business {
    member: number;
    state_of_inc: StateOfInc;
    s_corp_election_date: string | null;
    preferred_s_election_date: string | null;
    naics_code: string;
    naics_business_activity: string;
    industry: string;
    profession: string;
    prior_payroll_provider: string;
    activity_details: {
        [key: string]: {
            answer1: string;
            answer2: string;
            answer3?: string;
            answer4?: string;
            answer5?: string;
        };
    };
    has_staff: boolean;
}

export enum MemberStatus {
    PENDING = '---',
    CONFIRMED = 'OK',
    EMPLOYEE = 'EMP',
    OFFBOARDED = 'OFF',
    PAYMENT_UPDATE = 'payment_update',
    PAYMENT_ERROR = 'payment_error',
}

export interface Member {
    id: number;
    offboarding_call_link: string;
    business: Business;
    payroll_service_embedded_status?: string;
    bookkeeping_provider?: 'hurdlr';
    uuid: string;
    created: string;
    updated: string;
    sfaccountid?: string;
    sfcontactid?: string;
    sfopportunityid?: string;
    first_name: string;
    last_name: string;
    full_business_name: string;
    fullname: string;
    legal_first_name: string;
    legal_middle_name: string;
    legal_last_name: string;
    nickname: string;
    preferred_name: string;
    pronouns: string[];
    SSN: string;
    dob: string;
    citizenship: string;
    citizenshipcode: string;
    email: string;
    personalphonenumber: string;
    moving_out_state: boolean;
    moving_to_state: string;
    clients_outside_state: boolean;
    business_operated_last_year: boolean;
    business_activity_description: string;
    businessname: string;
    federal_business_name: string;
    incorporation: string;
    incsuffix: string;
    primaryactivity: string;
    primaryactivityother: string;
    establisheddate: string;
    EIN: string;
    entitynumber: string;
    registeredagent: string;
    stateofoperation: string;
    businessphonenumber: string;
    status: MemberStatus;
    usertype: string;
    cohortdate?: string;
    firstpayment: string;
    lastpayment?: string;
    mainstep: string;
    substep: string;
    stepnumber: number;
    discountcode: string;
    accounting_advisor: string;
    stripecustomerid: string;
    stripesubscriptionid: string;
    stripelast4: string;
    business_address: Address | null;
    mailing_address: Address | null;
    home_address: Address | null;
    one_line_business_address?: string;
    one_line_mail_address?: string;
    one_line_home_address?: string;
    consultingdescription: string;
    servicetype: string;
    othertype: string;
    additionalshareholdername: string;
    additionalshareholdershare: number;
    additionalshareholdersameaddress: boolean;
    taxation: string;
    annual_salary: string;
    salary_update_due: boolean;
    advised_salary_low?: string;
    advised_salary_high?: string;
    expected_profit?: string;
    user: number;
    member_relationship_manager?: string;
    has_formation_workflow: boolean;
    has_gusto_client: boolean;
    is_onboarding_completed: boolean;
    onboarding_completed_at?: string;
    // TODO :(@eng-onboarding) llc_tier_enabled is temporary and should be removed once the official LLC Tier product is built
    llc_tier_enabled: boolean;
    hasPaymentIssue?: boolean;
    trustpilot_survey_completed_at?: string;
    sms_opt_in: boolean;
    sms_transactional_opt_in?: boolean;
    sms_marketing_opt_in?: boolean;
    membership_tier: MembershipTier;
    // Nullable on the wire: members created before the stage backfill still serialize stage as null.
    stage?: 'onboarding' | 'launchpad' | 'activated' | 'offboarded' | null;
}
